<template>
  <div>
    <v-card id="positions-hday" class="px-4 pt-2 pb-4 my-0 elevation-0">
      <v-card-title class="px-1">
        <h1 class="card-title mb-1">
          {{ $t('views.dashboardDetail.yield.positionsHdayTable.title') }}
        </h1>
      </v-card-title>

      <v-data-table
        id="positions-hday-table"
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        sort-by="totalValueBaseCurrency"
        :sort-desc="true"
        class="elevation-0 mt-3 left-sticky-table"
        mobile-breakpoint="0"
        :loading="loading"
        :no-data-text="type ? $t(`views.portfolio.positionsTable.noPosition.${type}`) : ''"
        item-key="_id"
        :item-class="itemRowBackground"
        :footer-props="{
          'items-per-page-options': [5, 10, 15, 20, 30, 50, 100],
        }"
        :items-per-page="15"
        :dense="user.preferences.denseTables"
        @update:options="updateOptions"
      >
        <template v-slot:item.ticker="{ item }">
          <div class="ticker-column items-flex">
            <TableTicker :position="item" />
            <div v-if="item.pendingRecreate" class="ml-3">
              <TableCoreRecreating :text="$t('views.common.tableCoreRecreating.text')" />
            </div>
            <template v-else-if="item.totalCostWithComissionBaseCurrency === null">
              <div class="ml-3 mt-1">
                <WarningIcon :text="$t('views.stocks.stockTable.warning')" />
              </div>
            </template>
          </div>
        </template>

        <template v-slot:item.totalValueBaseCurrency="{ item }">
          <currency
            v-if="!item.pendingRecreate && item.totalValueBaseCurrency"
            :value="item.totalValueBaseCurrency"
            :currency="user.baseCurrency"
            strong
          />
          <span v-else></span>
        </template>

        <template v-slot:item.dailyPlBaseCurrency="{ item }">
          <PositionTablePL :position="item" isDaily type="net" />
        </template>
        <template v-slot:item.dailyPlBaseCurrencyPercent="{ item }">
          <PositionTablePL :position="item" isDaily type="percent" />
        </template>

        <template v-slot:item.hday.mtd.plBaseCurrency="{ item }">
          <TableNetPercent
            v-if="get(item, 'hday.mtd.plBaseCurrency', null)"
            :value="get(item, 'hday.mtd.plBaseCurrency', null)"
            :currency="user.baseCurrency"
            type="net"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.hday.mtd.plBaseCurrencyPercent="{ item }">
          <TableNetPercent
            v-if="get(item, 'hday.mtd.plBaseCurrencyPercent', null)"
            :value="get(item, 'hday.mtd.plBaseCurrencyPercent', null)"
            type="percent"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.hday.ytd.plBaseCurrency="{ item }">
          <TableNetPercent
            v-if="get(item, 'hday.ytd.plBaseCurrency', null)"
            :value="get(item, 'hday.ytd.plBaseCurrency', null)"
            :currency="user.baseCurrency"
            type="net"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.hday.ytd.plBaseCurrencyPercent="{ item }">
          <TableNetPercent
            v-if="get(item, 'hday.ytd.plBaseCurrencyPercent', null)"
            :value="get(item, 'hday.ytd.plBaseCurrencyPercent', null)"
            type="percent"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.hday.years1.plBaseCurrency="{ item }">
          <TableNetPercent
            v-if="get(item, 'hday.years1.plBaseCurrency', null)"
            :value="get(item, 'hday.years1.plBaseCurrency', null)"
            :currency="user.baseCurrency"
            type="net"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.hday.years1.plBaseCurrencyPercent="{ item }">
          <TableNetPercent
            v-if="get(item, 'hday.years1.plBaseCurrencyPercent', null)"
            :value="get(item, 'hday.years1.plBaseCurrencyPercent', null)"
            type="percent"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.hday.years3.plBaseCurrency="{ item }">
          <TableNetPercent
            v-if="get(item, 'hday.years3.plBaseCurrency', null)"
            :value="get(item, 'hday.years3.plBaseCurrency', null)"
            :currency="user.baseCurrency"
            type="net"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.hday.years3.plBaseCurrencyPercent="{ item }">
          <TableNetPercent
            v-if="get(item, 'hday.years3.plBaseCurrencyPercent', null)"
            :value="get(item, 'hday.years3.plBaseCurrencyPercent', null)"
            type="percent"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.hday.years5.plBaseCurrency="{ item }">
          <TableNetPercent
            v-if="get(item, 'hday.years5.plBaseCurrency', null)"
            :value="get(item, 'hday.years5.plBaseCurrency', null)"
            :currency="user.baseCurrency"
            type="net"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.hday.years5.plBaseCurrencyPercent="{ item }">
          <TableNetPercent
            v-if="get(item, 'hday.years5.plBaseCurrencyPercent', null)"
            :value="get(item, 'hday.years5.plBaseCurrencyPercent', null)"
            type="percent"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.plBaseCurrency="{ item }">
          <PositionTablePL :position="item" type="net" />
        </template>
        <template v-slot:item.plBaseCurrencyPercent="{ item }">
          <PositionTablePL :position="item" type="percent" />
        </template>
      </v-data-table>

      <div v-if="!loading" class="ml-2 mt-0 pb-4">
        <a @click="customHeaders">
          <v-icon color="primary" size="15" class="watchlist-data-table-create-link-icon">
            mdi-cog-outline
          </v-icon>
          <span class="ml-1">{{ $t('views.common.headersModal.title') }}</span>
        </a>
      </div>
    </v-card>

    <HeadersModal
      v-if="isHeadersModalActive"
      :isModalActive.sync="isHeadersModalActive"
      :tableName="tableName"
      :defaultHeaders="defaultHeaders"
    />
  </div>
</template>

<script>
import { get } from 'lodash'
import { mapState } from 'vuex'
import { getPositions } from '@/lib/api.js'
import tableMixin from '@/views/components/core/tableMixin.js'
import Currency from '@/views/components/common/Currency.vue'
import TableNetPercent from '@/views/components/common/TableNetPercent'
import WarningIcon from '@/views/components/common/WarningIcon'
import TableCoreRecreating from '@/views/components/common/positions/TableCoreRecreating'
import InfoIcon from '@/views/components/common/InfoIcon'
import PositionMarketPrice from '@/views/components/common/positions/PositionMarketPrice'
import PositionModal from '@/views/components/common/positions/PositionModal'
import TableTicker from '@/views/components/common/TableTicker'
import PositionTablePL from '@/views/components/common/positions/PositionTablePL'
import { getCurrencySymbol } from '@/lib/utils/currencies.js'

export default {
  components: {
    Currency,
    TableNetPercent,
    PositionMarketPrice,
    WarningIcon,
    TableCoreRecreating,
    InfoIcon,
    PositionModal,
    TableTicker,
    PositionTablePL,
  },
  mixins: [tableMixin],
  props: {
    type: {
      type: String,
      default: null,
    },
    filters: {
      type: Object,
      default: () => {},
    },
    showZeroPositions: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isPositionModalActive: false,
    loading: false,
    editing: false,
    editItem: {},
    loadTimeout: null,
    tableName: 'positionsHday',
  }),
  computed: {
    ...mapState(['user']),
    isMobile() {
      return this.$vuetify.breakpoint.xs
    },
    defaultHeaders() {
      return [
        {
          text: this.$t(`views.portfolio.positionsTable.ticker.all`),
          value: 'ticker',
          divider: true,
          customHideDisabled: true,
          customDragDisabled: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.totalValue')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalValueBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.dailyPl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'dailyPlBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.dailyPlPercent')}`,
          value: 'dailyPlBaseCurrencyPercent',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.mtd.pl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'hday.mtd.plBaseCurrency',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.mtd.plPercent')}`,
          value: 'hday.mtd.plBaseCurrencyPercent',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.ytd.pl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'hday.ytd.plBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.ytd.plPercent')}`,
          value: 'hday.ytd.plBaseCurrencyPercent',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.years1.pl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'hday.years1.plBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.years1.plPercent')}`,
          value: 'hday.years1.plBaseCurrencyPercent',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.years3.pl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'hday.years3.plBaseCurrency',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.years3.plPercent')}`,
          value: 'hday.years3.plBaseCurrencyPercent',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.years5.pl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'hday.years5.plBaseCurrency',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.years5.plPercent')}`,
          value: 'hday.years5.plBaseCurrencyPercent',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.pl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'plBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.plPercent')}`,
          value: 'plBaseCurrencyPercent',
          align: 'right',
        },
      ]
    },
    somePendingRecreatePosition() {
      return this.items.some((item) => item.pendingRecreate)
    },
  },
  watch: {
    async type() {
      if (this.type) await this.loadItems()
    },
    filters: {
      async handler() {
        await this.loadItems()
      },
      deep: true,
    },
  },
  async destroyed() {
    clearTimeout(this.loadTimeout)
  },
  methods: {
    get,
    itemRowBackground(item) {
      if (item.pendingRecreate) return 'row-recreating'
      else if (item.totalCostWithComissionBaseCurrency === null) return 'row-advise'
    },
    async loadItems() {
      // console.log('loadItems')
      this.loading = true

      const filters = { ...this.filters }
      if (!this.filters.ticker) {
        filters.ticker = { $ne: this.user.baseCurrency }
      }

      try {
        const filter = {
          type:
            this.type === 'all'
              ? undefined
              : this.type === 'allNeCash'
              ? { $ne: 'cash' }
              : this.type,
          positionNumber: this.showZeroPositions ? undefined : { $ne: 0 },
          ...filters,
        }
        const { page, limit, sort } = this.getTablePaginationOptions(this.options)
        const response = await getPositions({
          filter,
          page,
          limit,
          sort,
        })
        this.setTablePaginatedResponse(response)

        clearTimeout(this.loadTimeout)
        if (this.somePendingRecreatePosition) {
          this.loadTimeout = setTimeout(() => {
            this.loadItems()
          }, 5000)
        }

        this.filtersFirstLoaded = true // controlamos que ya cargamos por primera vez para no traer dos veces posiciones
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.loading = false
    },
  },
}
</script>

<style lang="scss">
#positions-hday-table {
  .v-data-table-header th {
    white-space: nowrap;
  }
  .v-data-table__expanded > td {
    background: #f7f8fb;
  }
  .v-data-table__expanded > td:nth-child(1) {
    background: #f7f8fb;
  }
  .items-flex {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    &.ticker-column {
      justify-content: flex-start;
      align-items: flex-start;
    }
  }
}
</style>
